/**
 * 🔒 An employment's positions, edited as one list in the Employment window and
 * saved as one span set (piece 13; decision 6660, amending 6446 and 0589). The
 * window, the phone and the server read the same rules here, so a list the
 * window lets through is the list the server writes, and a refusal reads the
 * same sentence wherever it is met.
 */

import { isCalendarDay } from '../zonedTime';

/** 🔒 The route Save sends the whole list to; it replaces the employment's positions in one transaction. */
export function employmentPositionsPath(employmentId: string): string {
  return `/pages/employments/${encodeURIComponent(employmentId)}/positions`;
}

/** One position as the Employments row carries it for the window: its title and its two days, as days. */
export interface EmploymentPositionRow {
  id: string;
  /** 🔒 The version the window read: a position is in piece 7's version domain. */
  version: number;
  title: string;
  startDate: string;
  /** Its last day; null on the latest position of an open employment. */
  endDate: string | null;
}

/** One row of the window's list: a kept position names its id and the version it read, and a new one names neither. */
export interface EmploymentPositionDraft {
  id: string | null;
  version?: number | null;
  title: string;
  startDate: string;
  endDate: string | null;
}

/** The positions a tile row carries as text, read back as rows; anything that is not one reads as none. */
export function positionListOf(json: unknown): EmploymentPositionRow[] {
  if (typeof json !== 'string' || json === '') return [];
  try {
    const rows: unknown = JSON.parse(json);
    if (!Array.isArray(rows)) return [];
    return rows.flatMap((row: Record<string, unknown>) => (typeof row?.id === 'string' && typeof row.startDate === 'string'
      ? [{ id: row.id, version: typeof row.version === 'number' ? row.version : 0, title: typeof row.title === 'string' ? row.title : '', startDate: row.startDate, endDate: typeof row.endDate === 'string' ? row.endDate : null }]
      : []));
  } catch {
    // A row that carries no readable list opens the window on the title it does carry.
    return [];
  }
}

/** The day before a day, both written as 2022-03-15. */
export function positionDayBefore(day: string): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

/** Whether the window's list differs from the one it opened on, in any position's title, days or presence. */
export function positionsChanged(opened: readonly EmploymentPositionDraft[], now: readonly EmploymentPositionDraft[]): boolean {
  const key = (row: EmploymentPositionDraft) => [row.id ?? '', row.title.trim(), row.startDate, row.endDate ?? ''].join('\u0000');
  return opened.length !== now.length || opened.some((row, index) => key(row) !== key(now[index]!));
}

/** The longest title a position holds. */
export const POSITION_TITLE_MAX = 300;

/** 🔒 The refusal a save meets when a kept position changed since the window read it (decision 6660). */
export const POSITIONS_STALE = 'A title of this employment was changed by someone else since you opened it. Reopen it to see the titles it holds now.';

/** Whether a text is a real calendar day written as 2022-03-15. */
export function isPositionDay(value: string | null | undefined): value is string {
  return isCalendarDay(value);
}

/** The list oldest first, by first day; two on one day keep the order they were sent in. */
export function positionsOldestFirst<T extends { startDate: string }>(list: readonly T[]): T[] {
  return list.map((row, index) => ({ row, index })).sort((a, b) => a.row.startDate.localeCompare(b.row.startDate) || a.index - b.index).map(({ row }) => row);
}

/**
 * 🔨 The refusal a list earns, or null (decision 6660). An employment holds one
 * position at least; every title is named, plain and of a sane length; every
 * day is a day and every last day falls on or after its first; each position
 * ends before the next begins, and the refusal names the two that overlap.
 * Only the latest is ever open: an open employment's latest stays open, since
 * ending is the end path's alone (decision 0165), and an ended employment's
 * latest names the employment's last day.
 */
export function positionListProblem(list: readonly EmploymentPositionDraft[], employment: { open: boolean }): string | null {
  if (!Array.isArray(list) || list.length === 0) return 'An employment holds one position at least, so the last one cannot be removed.';
  for (const row of list) {
    const title = typeof row?.title === 'string' ? row.title.trim() : '';
    if (title === '') return 'Every position names its title.';
    if (title.length > POSITION_TITLE_MAX || /[<>]/.test(title)) return 'A title is plain text of 300 characters or fewer.';
    if (!isPositionDay(row.startDate)) return `${title} names no first day; a day reads as 2022-03-15.`;
    if (row.endDate !== null && row.endDate !== undefined && !isPositionDay(row.endDate)) return `${title}'s last day is not a day; a day reads as 2022-03-31.`;
    if (row.endDate && row.endDate < row.startDate) return `${title}'s last day falls before its first.`;
  }
  const ordered = positionsOldestFirst(list);
  for (let i = 0; i < ordered.length - 1; i += 1) {
    const [held, next] = [ordered[i]!, ordered[i + 1]!];
    if (!held.endDate) return `${held.title.trim()} is not the latest position, so it names its last day.`;
    if (next.startDate <= held.endDate) {
      return `${held.title.trim()} (${held.startDate} to ${held.endDate}) and ${next.title.trim()} (from ${next.startDate}) overlap; each position ends before the next begins.`;
    }
  }
  const latest = ordered[ordered.length - 1]!;
  if (employment.open && latest.endDate) return 'The latest position of an open employment has no last day; End Employment ends the job.';
  if (!employment.open && !latest.endDate) return "An ended employment's latest position names its last day, which is the employment's own.";
  return null;
}
